/**
 * Utility responsible for resampling the reference sites based on the selected sites.
 */

import { getMeanSquaredError } from '../utils/processing';
import AlgorithmProcessor from "./../utils/algorithm-processor/algorithm-processor";

export default class Resampler {

  /**
   * Constructor to capture the references required by the Controller.
   *
   * @param variablesModel Used to loop through all variables to perform resampling.
   * @param referenceSitesHashModel The source data to resample.
   * @param selectedSitesModel The selected sites that impact the resampling.
   * @param algorithmsModel Identifies the algorithm to use for resampling.
   * @param siteDistanceModel Provide distance information for use during the resampling.
   */
  constructor(variablesModel, referenceSitesHashModel, selectedSitesModel, algorithmsModel,
              siteDistanceModel) {

    // Cache references.
    this._variablesModel = variablesModel;
    this._referenceSitesHashModel = referenceSitesHashModel;
    this._selectedSitesModel = selectedSitesModel;
    this._algorithmsModel = algorithmsModel;
    this._siteDistanceModel = siteDistanceModel;

    // Instantiate an AlgorithmProcessor and update when either the SiteDistanceModel or
    // AlgorithmModel changes.
    this._processor = new AlgorithmProcessor();

    this._siteDistanceModel.on(this._siteDistanceModel.EVENT_DATA_CHANGED, () => {
      this._processor.siteDistanceData = this._siteDistanceModel.getData();
    });

  }

  /**
   * Perform the resample.
   */
  resample() {

    // Cache the reference sites for easy use.
    const referenceSitesHashMap = this._referenceSitesHashModel.getData();

    // Build the temporary cache which will gather the results. This is initially populated with
    // the reference site info.
    let _resampledCache = {};
    Object.keys(referenceSitesHashMap).forEach(_id => {
      const _referenceSite = referenceSitesHashMap[_id];
      _resampledCache[_id] = {
        id: _id,
        lat: _referenceSite.lat,
        lon: _referenceSite.lon,
        isSelectable: _referenceSite.isSelectable,
        data: {}
      };
    });

    // Identify the selected algorithm.
    const selectedAlgorithm = this._algorithmsModel.getSelectedItem();
    if (selectedAlgorithm) {
      this._processor.algorithm = selectedAlgorithm.id;
    }

    // Identify the selected sites. This will be passed later to the processing algorithm for
    // performing calculations.
    let selectedSiteIds = Object.keys(this._selectedSitesModel.getData());

    // Only perform processing if sites are actually selected.
    if (selectedSiteIds.length > 0) {

      // Loop through every variable as we are re-calculating for ALL variables.
      this._variablesModel.getItems().forEach((_variable) => {

        // Only process if numeric data.
        const variableFieldName = _variable.fieldName;
        if (!_variable.isNumerical) {

          // Not numeric data, so just copy data from reference site.
          Object.keys(referenceSitesHashMap).forEach(_id => {
            const _referenceSite = referenceSitesHashMap[_id];
            const _resampledSite = _resampledCache[_id];
            _resampledSite.data[variableFieldName] = _referenceSite.data[variableFieldName];
          });

        } else {

          // Numeric data, so process data.

          // Transform the referenceSitesModel (array) to `referenceData` (object, keyed on siteId)
          // which is the format required by the 'processStrategy' method.
          const referenceData = {};
          const hashMap = this._referenceSitesHashModel.getData();
          Object.keys(hashMap).forEach(id => {
            const _referenceSite = hashMap[id];

            // Actual data (values) is passed to the 'processStrategy' method via the 'data' array.
            // For non-temporal datasets, the 'data' array will contain only a single value
            // representing the actual data for the variable being processed. For temporal datasets,
            // multiple records relate to a single site, so all records for each single site must be
            // concatenated into the single 'data' array.

            // Based on the unique id, determine if the site has previously been added to the
            // 'referenceData' being built. This would only happen where multiple records exist for
            // each site (ie: temporal data).
            let site = referenceData[id];
            if (!site) {
              // Site has not previously been added to 'referenceData', so create a base 'site' object
              // and add it to 'referenceData'. We will then continue populating the 'site' object.
              // Note that the structure of the 'site' object is dependent on the requirements of the
              // 'processStrategy' method.
              site = {
                lat: _referenceSite.lat,
                lon: _referenceSite.lon,
                index: _referenceSite.index,
                data: []
              };
              referenceData[id] = site;

            }

            // We now have a 'site' object representing the site, which has previously (or now) been
            // added to the 'referenceData'. Time to finish populating the 'site' object.
            // If the dataset is temporal, determine where the value should be added to the 'data'
            // array, based on the sample data. Again, this is specific to GBRF datasets. For non-
            // temporal datasets, only a single value will appear in the 'data' array, representing the
            // current variable value.
            if (_referenceSite.data[variableFieldName] !== undefined) {
              site.data.push(_referenceSite.data[variableFieldName]);
            } else {
              delete referenceData[id]
            }

          });

          // Invoke the resampling algorithm.
          const processStrategyResult = this._processor.process(referenceData, selectedSiteIds);

          _variable.meanSquaredError = getMeanSquaredError(referenceData, processStrategyResult);

          // Populate the internal cache with the results of the 'processStrategy' method.
          Object.keys(processStrategyResult).forEach((_id) => {
            const _processStrategyResult = processStrategyResult[_id];

            // Only a single value is expected, which represents the value of the variable being
            // processed.
            let _resampledSite = _resampledCache[_id];
            _resampledSite.data[variableFieldName] = _processStrategyResult.data[0];

          });

        }

      });

    } else {

      // No sites selected, so just use reference data.
      const hashMap = this._referenceSitesHashModel.getData();
      Object.keys(hashMap).forEach((id) => {
        _resampledCache[id] = hashMap[id];
      });

    }

    // Update the internal cache.
    return _resampledCache;

  }

}



// WEBPACK FOOTER //
// ./src/app/models/resampler.js